/**
 * Decides, before any WebContainer boot, how much of the workbench this device can run.
 *
 * - `full`: desktop Chromium with at least 8 GB (`navigator.deviceMemory`) and OPFS. Prewarm
 *   is allowed only here.
 * - `best-effort`: desktop Firefox or Safari, Chromium with 4 GB or unknown memory, or no OPFS
 *   (private mode: no dependency cache). Boots on demand, never prewarms.
 * - `view-only`: phones, tablets, in-app browsers, unknown engines, under 4 GB, or no
 *   cross-origin isolation / `SharedArrayBuffer`. Never boots; the panel explains why.
 *
 * Plan: PROTOTYPER_STABILITY_TECH_PLAN.md §6 (P2.3).
 */

export type WorkbenchDeviceTier = 'full' | 'best-effort' | 'view-only';

export type WorkbenchDeviceReason =
  | 'not-cross-origin-isolated'
  | 'no-shared-array-buffer'
  | 'mobile'
  | 'in-app-browser'
  | 'unsupported-browser'
  | 'very-low-memory'
  | 'low-memory'
  | 'unknown-memory'
  | 'browser-best-effort'
  | 'no-opfs'
  | 'user-override';

export interface WorkbenchCapability {
  tier: WorkbenchDeviceTier;
  reasons: WorkbenchDeviceReason[];
}

export interface WorkbenchDeviceEnv {
  crossOriginIsolated: boolean;
  sharedArrayBuffer: boolean;
  userAgent: string;
  platform?: string;
  maxTouchPoints?: number;
  /** `navigator.deviceMemory` in GB; `undefined` when the browser does not expose it. */
  deviceMemory?: number;
  opfs: boolean;
  /** `VITE_WORKBENCH_DEVICE_TIERS=0` turns tiers off: only the hard requirements apply. */
  tiersEnabled?: boolean;
  /** The founder chose "Try anyway" on this device (see `allowWorkbenchOnThisDevice`). */
  userOverride?: boolean;
}

const MOBILE_UA = /Android|iPhone|iPod|iPad|Mobile|Silk|Kindle|Opera Mini/i;
const IN_APP_UA =
  /FBAN|FBAV|Instagram|LinkedInApp|Line\/|MicroMessenger|Snapchat|TikTok|Twitter|; wv\)/i;

/** Reasons a "Try anyway" override may lift; hardware and API gaps cannot be lifted. */
const OVERRIDABLE: ReadonlySet<WorkbenchDeviceReason> = new Set(['very-low-memory']);

function engineOf(userAgent: string): 'chromium' | 'firefox' | 'safari' | 'other' {
  if (/Firefox\//.test(userAgent)) return 'firefox';
  if (/Edg\/|Chrome\/|Chromium\//.test(userAgent)) return 'chromium';
  if (/Safari\//.test(userAgent)) return 'safari';
  return 'other';
}

export function classifyWorkbenchDevice(env: WorkbenchDeviceEnv): WorkbenchCapability {
  const hard: WorkbenchDeviceReason[] = [];
  if (!env.crossOriginIsolated) hard.push('not-cross-origin-isolated');
  if (!env.sharedArrayBuffer) hard.push('no-shared-array-buffer');
  if (hard.length > 0) return { tier: 'view-only', reasons: hard };
  if (env.tiersEnabled === false) return { tier: 'full', reasons: [] };

  // iPadOS reports a desktop "Macintosh" user agent; touch points give it away.
  const iPadDesktopMode = env.platform === 'MacIntel' && (env.maxTouchPoints ?? 0) > 1;
  if (MOBILE_UA.test(env.userAgent) || iPadDesktopMode)
    return { tier: 'view-only', reasons: ['mobile'] };
  if (IN_APP_UA.test(env.userAgent)) return { tier: 'view-only', reasons: ['in-app-browser'] };

  const engine = engineOf(env.userAgent);
  if (engine === 'other') return { tier: 'view-only', reasons: ['unsupported-browser'] };

  const reasons: WorkbenchDeviceReason[] = [];
  let tier: WorkbenchDeviceTier;
  const memory = env.deviceMemory;

  if (memory !== undefined && memory < 4) {
    tier = 'view-only';
    reasons.push('very-low-memory');
  } else if (engine !== 'chromium') {
    tier = 'best-effort';
    reasons.push('browser-best-effort');
  } else if (memory === undefined) {
    tier = 'best-effort';
    reasons.push('unknown-memory');
  } else if (memory < 8) {
    tier = 'best-effort';
    reasons.push('low-memory');
  } else {
    tier = 'full';
  }

  if (tier === 'full' && !env.opfs) {
    tier = 'best-effort';
    reasons.push('no-opfs');
  }

  if (tier === 'view-only' && env.userOverride && reasons.every((r) => OVERRIDABLE.has(r))) {
    return { tier: 'best-effort', reasons: [...reasons, 'user-override'] };
  }
  return { tier, reasons };
}

/** True when the panel may offer "Try anyway" for this result. */
export function canOverrideWorkbenchTier(capability: WorkbenchCapability): boolean {
  return (
    capability.tier === 'view-only' &&
    capability.reasons.length > 0 &&
    capability.reasons.every((r) => OVERRIDABLE.has(r))
  );
}

const OVERRIDE_KEY = 'ideoz:workbench:device-override:v1';

function readOverride(): boolean {
  try {
    return globalThis.localStorage?.getItem(OVERRIDE_KEY) === '1';
  } catch {
    return false;
  }
}

/** Remembers "Try anyway" on this device. The caller reloads so the next boot sees it. */
export function allowWorkbenchOnThisDevice(): void {
  try {
    globalThis.localStorage?.setItem(OVERRIDE_KEY, '1');
  } catch {
    /* private mode: the override lasts for this page only */
  }
  cached = null;
}

let cached: WorkbenchCapability | null = null;

export function detectWorkbenchCapability(): WorkbenchCapability {
  if (cached) return cached;
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return { tier: 'view-only', reasons: ['unsupported-browser'] };
  }
  const nav = navigator as Navigator & { deviceMemory?: number };
  const flag = import.meta.env?.VITE_WORKBENCH_DEVICE_TIERS;
  cached = classifyWorkbenchDevice({
    crossOriginIsolated: Boolean(globalThis.crossOriginIsolated),
    sharedArrayBuffer: typeof SharedArrayBuffer !== 'undefined',
    userAgent: nav.userAgent ?? '',
    platform: nav.platform,
    maxTouchPoints: nav.maxTouchPoints,
    deviceMemory: typeof nav.deviceMemory === 'number' ? nav.deviceMemory : undefined,
    opfs: typeof nav.storage?.getDirectory === 'function',
    tiersEnabled: !(flag === '0' || flag === 'false'),
    userOverride: readOverride(),
  });
  return cached;
}

export class WorkbenchUnsupportedDeviceError extends Error {
  readonly capability: WorkbenchCapability;

  constructor(capability: WorkbenchCapability) {
    super(
      `The prototype workspace does not run on this device (${capability.reasons.join(', ')}).`
    );
    this.name = 'WorkbenchUnsupportedDeviceError';
    this.capability = capability;
  }
}
